/**
 * HOW A CASE GOT ITS ATTORNEY — the vocabulary half.
 *
 * ATTORNEY_BRIEF Part 1B: each case is assigned MECHANICALLY among the attorneys
 * registered for that parcel's registry area, with no person at the platform
 * selecting. Before this, assignment was an admin naming a professional by user
 * id, and the platform's answer to "why did this attorney get this case" was "an
 * admin picked them" — a human choosing who receives paid work, at a company that
 * charges the client a fee, which is the steering COMPLIANCE Constraint 1
 * (Supreme Court Rules 1988, Rule 39) exists to police.
 *
 * TODO-BLOCKED-Q1E. All of this presumes ATTORNEY_BRIEF Q1(e) — that an
 * Attorney-at-Law may accept referrals from a panel operated by a fee-charging
 * non-attorney company — is answered yes. It is unanswered.
 *
 * NO FEE, PRICE, RATE OR PAYOUT APPEARS HERE OR MAY EVER (D1 is open, CLAUDE.md
 * non-negotiable 3), and nothing here ranks, rates or orders a professional: the
 * rotation's order is DERIVED at the moment of assignment from stored facts, so
 * there is no stored ranking to read, display, or tamper with.
 */
import { z } from "zod";
/**
 * How the assignee on an assignment row was chosen. A STORED FACT, not an
 * inference from log parsing: "what proportion of cases were assigned
 * mechanically" must be one `GROUP BY`, because it is the question the reviewing
 * attorney will actually ask. Same reasoning as `documents.kind` — a
 * compliance-load-bearing fact belongs in a column, not a comment.
 *
 * Mirrored as the `assignment_selection_method` enum in migration 0024. If they
 * drift, the database wins.
 */
export const ASSIGNMENT_SELECTION_METHODS = [
    /**
     * The default and the point of the feature: the deterministic rule below chose,
     * over the panel for the parcel's registry area. No person selected.
     */
    "panel_rotation",
    /**
     * A DECLARED EXCEPTION. An admin named the assignee. It is deliberately a
     * separate value rather than a flag on the default path, so that an override
     * cannot be made to look like a rotation, and so that overrides are countable.
     * It always carries an `override_reason`, enforced by trigger.
     */
    "admin_override",
    /**
     * WROTE ITSELF INTO HISTORY, and can never be written again. Assignments made
     * before the panel model existed were an admin hand-picking a professional with
     * no method declared and no reason recorded. Backfilling them as
     * `panel_rotation` would claim a rotation that never ran, and as
     * `admin_override` would invent a reason nobody gave — both are fabrications
     * (CLAUDE.md non-negotiable 1). So the unknown gets a name of its own instead
     * of a blank that reads like an answer. Migration 0024's
     * `assignment_selection_is_declared` trigger refuses it on any new row.
     */
    "undeclared_pre_panel",
];
/**
 * Why an admin overrode the rotation. Codes, not prose — the decision reaches the
 * append-only audit log, which can never be erased and therefore must never carry
 * personal data (COMPLIANCE Constraint 4).
 *
 * READ BEFORE ADDING A CODE. The override is the steering risk this feature was
 * built to remove, kept alive only because a panel member really can be
 * unavailable. Every code here describes a fact about THIS CASE or THIS
 * ASSIGNEE's availability. A code meaning "we would rather this attorney had it"
 * is the thing mechanical assignment exists to prevent and does not belong here.
 *
 * WHAT MAKES THE OVERRIDE SURVIVABLE, and it is not this list. Two things, and
 * the second is what makes the first mean anything:
 *
 *  1. AN OVERRIDE NAMES A PANEL MEMBER FOR THE PARCEL'S REGISTRY (migration
 *     0026). It is an exception to whose turn it is, never to where the land is.
 *     Until 0026 it was neither: the jurisdiction trigger was gated on the
 *     mechanical method, so an override could hand a case to any verified
 *     attorney in the country, and an operator who wanted a particular attorney
 *     simply overrode every time.
 *  2. An override assignment still counts as a turn taken in the rotation (the
 *     ordering query does not filter on `selection_method`). So an override can
 *     move work EARLIER to a particular attorney; it cannot increase anyone's
 *     total share, because the recipient goes to the back of the queue exactly as
 *     if the rule had chosen them.
 *
 * (2) ONLY BECAME TRUE WITH (1). The ordering is computed over panel members, so
 * an assignment to somebody with no seat moved nobody's position — the cap
 * applied to the overrides that did not need capping and not to the ones that
 * did. Said plainly here because this paragraph previously claimed the cap
 * unconditionally.
 *
 * Mirrored as a CHECK-equivalent trigger in migration 0024. If they drift, the
 * database wins.
 */
export const ASSIGNMENT_OVERRIDE_REASONS = [
    /** The attorney the rotation chose said no to this case. */
    "ASSIGNEE_DECLINED",
    /** They cannot take it now — and not for long enough to be worth a panel pause. */
    "ASSIGNEE_UNAVAILABLE",
    /** The attorney declared a conflict. Their declaration, recorded, not our finding. */
    "CONFLICT_OF_INTEREST_DECLARED",
    /** The client already instructs this attorney on this matter. */
    "PREEXISTING_CLIENT_RELATIONSHIP",
    /** Ops had to act and none of the above fits. Deliberately the vaguest, and countable. */
    "OPS_ESCALATION",
    /**
     * THERE IS DELIBERATELY NO CODE MEANING "NO PANEL MEMBER IN THIS AREA".
     * If the parcel's registry has nobody available, the honest outcome is that the
     * case WAITS at ROUTED — not that it quietly leaves the jurisdiction. The
     * search happens where the parcel is registered (CLAUDE.md §5); an attorney
     * with no standing at that registry is worse than no attorney at all, because
     * the client pays for a trip to an office with no jurisdiction over their land.
     * `NoEligiblePanelMemberError` is that outcome, and it is loud.
     */
];
export function isAssignmentOverrideReason(value) {
    return ASSIGNMENT_OVERRIDE_REASONS.includes(value);
}
/**
 * THE RULE, VERSIONED — written into every mechanical assignment's audit entry.
 *
 * Least-recently-assigned within the panel for the parcel's registry area, ties
 * broken by longest-standing membership, then by a per-case digest. Recorded as a
 * version string so that a later change to the rule is VISIBLE in the record
 * rather than silently rewriting the meaning of every past assignment: two rows
 * carrying different rule ids are honestly two different rules, and a reviewer
 * comparing them can see it without reading the git history.
 *
 * LRA rather than random, and the reasons are the argument to put to the
 * reviewing attorney at ATTORNEY_BRIEF Q1(f):
 *
 *  1. It answers "why not me" with a fact — "you were assigned on the 4th, they
 *     were last assigned on the 28th" — and both dates are stored rows. Random
 *     answers "the generator said so", which is unverifiable without a seed whose
 *     provenance is independent of the platform.
 *  2. Bounded wait: with n live members nobody waits more than n−1 cases. Random
 *     produces long runs that look like favouritism even when honest.
 *  3. A third party — the reviewing attorney, or an excluded member — can
 *     RECOMPUTE it from `assignments`, `cases.registry_district` and
 *     `panel_memberships` alone, without trusting our logs.
 */
export const PANEL_ROTATION_RULE = "PANEL_ROTATION_LRA_V1";
/**
 * Which tiebreak, if any, decided a rotation — recorded in the audit entry so the
 * ordering can be checked rather than taken on trust.
 *
 * WRITTEN BY THE DATABASE, NOT BY ANY CODE HERE (0028 §3). A TypeScript helper
 * used to compute it beside the entry, which meant the ordering rule had two
 * implementations that could disagree; there is now one, in `tv_panel_census`.
 * This list is therefore a DESCRIPTION of what that trigger emits, and
 * `panel-assignment.test.ts` asserts the database writes nothing outside it — so
 * it stays a description of what happens rather than of what used to.
 */
export const ROTATION_TIE_BREAKS = [
    /** The chosen member was strictly least-recently-assigned. No tie. */
    "NONE",
    /** Two or more were level on last-assigned; the longest-standing member won. */
    "JOINED_AT",
    /** Level on both; a per-case digest decided. Recomputable, and not steerable. */
    "PER_CASE_DIGEST",
];
/**
 * THE ONE OVERRIDE VALIDATION, shared by the API route and the admin form — the
 * same rule as `panelMembershipInputSchema`: one schema, every door (CLAUDE.md
 * §2A). The web form parses against this exact object before it sends, so an
 * operator gets a named field back instead of a bare 400, and the server parses
 * it again because a client-side check enforces nothing.
 *
 * NOTE WHAT THE ASSIGNEE FIELD IS: a user id, TYPED, not chosen from a list. The
 * mechanical door takes no body at all and cannot express a hand-pick; this one
 * must, because the whole point of an override is that a person names someone.
 * What it still must not do is offer a fetched roster of verified attorneys to
 * pick from — that is a directory at the moment work is handed out, which is the
 * shape COMPLIANCE Constraint 1 (Rule 39) forbids inside the admin panel exactly
 * as at the client edge. The operator carries the id over from the roster screen,
 * the same way a panel seat is entered.
 *
 * THE REASON IS REQUIRED AND IS A CODE. Free text would reach the append-only
 * audit log, which can never be erased and therefore must never carry personal
 * data (COMPLIANCE Constraint 4); a code says what happened without naming
 * anyone, and makes overrides countable in one `GROUP BY`.
 */
export const assignmentOverrideInputSchema = z.object({
    professionalUserId: z.string().trim().uuid("professionalUserId must be a user id (UUID)"),
    reason: z.enum(ASSIGNMENT_OVERRIDE_REASONS),
});
//# sourceMappingURL=assignment.js.map